import { useEffect, useRef, useState } from "react";
import { Flag, Send } from "lucide-react";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";

const REPORT_REASONS = [
  { value: "insultes", label: "Insultes" },
  { value: "harcelement", label: "Harcèlement" },
  { value: "spam", label: "Spam" },
  { value: "autre", label: "Autre" },
];

export default function GameChatBar({
  you,
  messages = [],
  opponentUserId,
  onSend,
  onReport,
  disabled = false,
}) {
  const [text, setText] = useState("");
  const [reportTarget, setReportTarget] = useState(null);
  const [reportReason, setReportReason] = useState("insultes");
  const [reporting, setReporting] = useState(false);
  const myScrollRef = useRef(null);
  const oppScrollRef = useRef(null);

  const myMessages = messages.filter((m) => m.from === you);
  const opponentMessages = messages.filter((m) => m.from !== you);

  useEffect(() => {
    if (myScrollRef.current) {
      myScrollRef.current.scrollTop = myScrollRef.current.scrollHeight;
    }
  }, [myMessages.length]);

  useEffect(() => {
    if (oppScrollRef.current) {
      oppScrollRef.current.scrollTop = oppScrollRef.current.scrollHeight;
    }
  }, [opponentMessages.length]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const t = text.trim();
    if (!t || disabled) return;
    onSend(t);
    setText("");
  };

  const confirmReport = async () => {
    if (!reportTarget || !onReport) return;
    setReporting(true);
    try {
      await onReport({
        msg_id: reportTarget.msg_id,
        reported_user_id: reportTarget.user_id || opponentUserId,
        text: reportTarget.text,
        reason: reportReason,
      });
    } finally {
      setReporting(false);
      setReportTarget(null);
      setReportReason("insultes");
    }
  };

  return (
    <>
      <div
        className="w-full max-w-[560px] mx-auto mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3"
        data-testid="online-chat-bar"
      >
        {/* Bas gauche : vos messages + saisie */}
        <div className="glass rounded-lg flex flex-col min-h-[140px] max-h-[200px] sm:max-h-[220px]" data-testid="online-chat-mine">
          <div className="px-3 py-2 border-b border-[color:var(--border)] text-[10px] tracking-[0.2em] text-[color:var(--text-secondary)]">
            VOS MESSAGES
          </div>
          <div ref={myScrollRef} className="flex-1 overflow-y-auto px-3 py-2 space-y-1.5 text-sm min-h-[60px]">
            {myMessages.length === 0 && (
              <p className="text-xs text-[color:var(--text-secondary)] text-center py-4">Aucun message envoyé.</p>
            )}
            {myMessages.map((m, i) => (
              <div
                key={m.msg_id || `mine-${i}`}
                className="p-2 rounded-md bg-[color:var(--primary)]/15 border border-[color:var(--primary)]/30 text-right"
              >
                <div>{m.text}</div>
              </div>
            ))}
          </div>
          <form onSubmit={handleSubmit} className="p-2 border-t border-[color:var(--border)] flex gap-2">
            <input
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Écrire à l'adversaire…"
              disabled={disabled}
              maxLength={300}
              className="flex-1 min-w-0 bg-[color:var(--surface)] border border-[color:var(--border)] rounded-md px-2 py-1.5 text-sm outline-none focus:border-[color:var(--primary)] disabled:opacity-50"
              data-testid="online-chat-input"
            />
            <button
              type="submit"
              disabled={disabled || !text.trim()}
              className="px-2.5 py-1.5 btn-gold rounded-md disabled:opacity-50 flex items-center gap-1 text-xs shrink-0"
              data-testid="online-chat-send"
            >
              <Send size={14} />
              <span className="hidden xs:inline">Envoyer</span>
            </button>
          </form>
        </div>

        {/* Bas droite : messages adversaire */}
        <div className="glass rounded-lg flex flex-col min-h-[140px] max-h-[200px] sm:max-h-[220px]" data-testid="online-chat-opponent">
          <div className="px-3 py-2 border-b border-[color:var(--border)] text-[10px] tracking-[0.2em] text-[color:var(--text-secondary)]">
            ADVERSAIRE
          </div>
          <div ref={oppScrollRef} className="flex-1 overflow-y-auto px-3 py-2 space-y-1.5 text-sm min-h-[100px]">
            {opponentMessages.length === 0 && (
              <p className="text-xs text-[color:var(--text-secondary)] text-center py-4">Aucun message reçu.</p>
            )}
            {opponentMessages.map((m, i) => (
              <div
                key={m.msg_id || `opp-${i}`}
                className="p-2 rounded-md bg-[color:var(--surface)] border border-[color:var(--border)] group relative"
              >
                <div className="text-[10px] tracking-[0.15em] text-[color:var(--text-secondary)] mb-0.5 pr-8">
                  {m.name}
                </div>
                <div>{m.text}</div>
                {onReport && opponentUserId && (
                  <button
                    type="button"
                    onClick={() => setReportTarget(m)}
                    className="absolute top-1.5 right-1.5 p-1 rounded text-rose-400/70 hover:text-rose-400 hover:bg-rose-500/10 opacity-60 group-hover:opacity-100 transition"
                    title="Signaler ce message"
                    data-testid="online-chat-report"
                  >
                    <Flag size={12} />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      <AlertDialog open={!!reportTarget} onOpenChange={(open) => !open && setReportTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Signaler ce message ?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-left">
                <p className="text-sm text-[color:var(--text-secondary)]">
                  Ce signalement sera transmis aux modérateurs. Merci de ne signaler que les contenus inappropriés.
                </p>
                {reportTarget && (
                  <blockquote className="text-sm border-l-2 border-[color:var(--border)] pl-3 italic">
                    « {reportTarget.text} »
                  </blockquote>
                )}
                <div>
                  <label className="text-xs tracking-[0.15em] text-[color:var(--text-secondary)] mb-1 block">
                    MOTIF
                  </label>
                  <Select value={reportReason} onValueChange={setReportReason}>
                    <SelectTrigger className="bg-[color:var(--surface)] border-[color:var(--border)]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {REPORT_REASONS.map((r) => (
                        <SelectItem key={r.value} value={r.value}>
                          {r.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={reporting}>Annuler</AlertDialogCancel>
            <button
              type="button"
              onClick={confirmReport}
              disabled={reporting}
              className="inline-flex h-10 items-center justify-center rounded-md bg-rose-600 px-4 py-2 text-sm font-medium text-white hover:bg-rose-700 disabled:opacity-50"
            >
              {reporting ? "Envoi…" : "Signaler"}
            </button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
